import { useEffect, useState } from 'react';
import { get } from '../utils/api';
import { getCurrentGameTimestamp } from '../config/dateTimeConfig';
import { FREE_CATEGORIES } from '../config/categoryConfig';

/**
 * Yesterday's answers for /answers.
 *
 * Scope (owner decision, 2026-11-03): the previous game day only, the free
 * categories only, plus that day's Compare the Pair. The backend enforces
 * this; the endpoint takes no parameters.
 */

export interface YesterdayAnswer {
  category: string;
  symbol: string;
  /** null when the name says AI: show the ticker only. */
  companyName: string | null;
  description: string | null;
}

export interface YesterdayPairQuestion {
  metricKey: string;
  question: string;
  winner: 'A' | 'B';
}

export interface YesterdayPair {
  symbolA: string;
  nameA: string | null;
  symbolB: string;
  nameB: string | null;
  industry: string | null;
  questions: YesterdayPairQuestion[];
}

export interface YesterdayAnswers {
  date: string;
  answers: YesterdayAnswer[];
  compareThePair: YesterdayPair | null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const FREE = new Set<string>(FREE_CATEGORIES);

/**
 * True when a date's puzzle is over: strictly before today's game date
 * (the game resets at 02:00 UTC). The backend applies the same rule; this is
 * a second guard so the page never shows the puzzle that is still in play.
 */
export function isPublishedAnswerDate(date: string, gameDate: string = getCurrentGameTimestamp()): boolean {
  return ISO_DATE.test(date) && date < gameDate;
}

/** "2026-11-02" -> "November 2, 2026" (UTC, so it never shifts a day). */
export function formatAnswerDate(date: string, month: 'long' | 'short' = 'long'): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month,
    day: 'numeric',
    year: 'numeric',
  });
}

/** True when text has AI as a standalone word in any case, or .ai, or A.I. */
export function mentionsAI(text: string): boolean {
  return /(^|[^a-z0-9])ai([^a-z0-9]|$)/i.test(text) || /(^|[^a-z0-9])a\.i\./i.test(text);
}

/** The name to show for a company: the ticker alone when the name is missing or says AI. */
export function answerLabel(name: string | null | undefined, symbol: string): string {
  return name && !mentionsAI(name) ? name : symbol;
}

const MAX_META_DESCRIPTION = 160;

/** Meta description from yesterday's answers: the date plus up to 3 names, never over 160 characters. */
export function buildDescription(
  date: string,
  answers: Array<{ companyName: string | null; symbol: string }>,
): string {
  const prefix = `Yesterday's Wallstreetle answers for ${formatAnswerDate(date, 'short')}`;
  const names = answers.map(a => {
    const label = answerLabel(a.companyName, a.symbol);
    return label === a.symbol ? a.symbol : `${label} (${a.symbol})`;
  });
  const suffix = ', plus the Compare the Pair result.';
  for (let count = Math.min(3, names.length); count >= 0; count--) {
    const text = count > 0 ? `${prefix}: ${names.slice(0, count).join(', ')}${suffix}` : `${prefix}${suffix}`;
    if (text.length <= MAX_META_DESCRIPTION) return text;
  }
  return `${prefix}.`;
}

type Status = 'loading' | 'ready' | 'error';

/**
 * Loads yesterday's answers. A response whose date is not strictly before the
 * client's game date is dropped (shown as empty), and any category other than
 * the free ones is filtered out, so a bad server response can never put a
 * live or members-only answer on the page.
 */
export function useYesterdayAnswers() {
  const [data, setData] = useState<YesterdayAnswers | null>(null);
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    let cancelled = false;
    get<YesterdayAnswers>('/api/answers/yesterday')
      .then(res => {
        if (cancelled) return;
        const body = res.data;
        if (!body || !isPublishedAnswerDate(body.date)) {
          setData(null);
          setStatus('ready');
          return;
        }
        setData({
          date: body.date,
          answers: (body.answers ?? []).filter(a => FREE.has(a.category) && !mentionsAI(a.symbol)),
          compareThePair:
            body.compareThePair && !mentionsAI(body.compareThePair.symbolA) && !mentionsAI(body.compareThePair.symbolB)
              ? body.compareThePair
              : null,
        });
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { data, status };
}
